import { allFunnelHeaderMilestones } from "src/app/shared/routes/global-route.constant";
import {
  startPagePath,
  summaryPagePath,
} from "src/app/shared/routes/quote/term-life-insurance/quote-route.constant";
import {
  CustomQueryParamsHandling,
  CustomRoute,
  getFullHtmlTitle,
  Page,
  TermLifeInsuranceDigitalApplicationFunnelStepNumber,
  TermLifeInsuranceLegacyApplicationFunnelStepNumber,
  Type,
} from "src/app/shared/routes/shared/shared-route.constant";
import { Product } from "src/app/shared/store/shared/product";
import {
  applicationDigitalSignatureAutomaticRedirectPagePath,
  applicationDigitalSignatureDeclinedPagePath,
  applicationDigitalSignatureErrorPagePath,
  applicationDigitalSignatureSuccessPagePath,
  applicationExpiredPagePath,
  applicationLimitedOfferPagePath,
  applicationPdfPagePath,
  applicationUploadIdPagePath,
} from "../../../shared/routes/application/term-life-insurance/application-route.constant";
import { getApplicationAndCheckExpirationGuard } from "../../shared/guards/get-application-and-check-expiration.guard";

export const applicationTermLifeInsuranceRoutes: CustomRoute[] = [
  {
    path: applicationLimitedOfferPagePath,
    loadComponent: async () =>
      (await import("../../shared/pages/limited-offer/limited-offer.page"))
        .LimitedOfferPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      page: Page.APPLICATION_LIMITED_OFFER,
      htmlTitle: getFullHtmlTitle($localize`Antragsdokumente`),
      pageTitle: $localize`Ihre Antragsdokumente`,
      pageSubtitle: $localize`Aufgrund der Versicherungssumme oder Ihres Risikoprofils sind zusätzliche Angaben zu Ihrer Gesundheit notwendig. Der digitale Abschluss ist leider nicht möglich.`,
      navigation: {
        previousNavigationPage: {
          pagePath: summaryPagePath,
        },
        nextNavigationPage: {
          pagePath: startPagePath,
          queryParamsHandling: CustomQueryParamsHandling.REPLACE,
        },
      },
      dataAnalytics: {
        funnelStepNumber:
          TermLifeInsuranceLegacyApplicationFunnelStepNumber.LIMITED_OFFER,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationExpiredPagePath,
    loadComponent: async () =>
      (
        await import(
          "../../shared/pages/application-has-expired/application-has-expired"
        )
      ).ApplicationHasExpiredPage,
    data: {
      type: Type.PRODUCT_AGNOSTIC,
      page: Page.APPLICATION_HAS_EXPIRED,
      htmlTitle: getFullHtmlTitle($localize`Zugriff abgelaufen`),
      pageTitle: $localize`Der Zugriff auf die Applikation ist leider abgelaufen ... 😢`,
    },
  },
  {
    path: applicationPdfPagePath,
    loadComponent: async () =>
      (await import("../../shared/pages/application-pdf/application-pdf.page"))
        .ApplicationPdfPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_PDF,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Antrag`),
      pageTitle: $localize`Ihr Antrag`,
      pageSubtitle: $localize`Ihr Antrag ist fertig und steht hier zum Download bereit. Im nächsten Schritt können Sie den Antrag online unterschreiben.`,
      funnelHeaderMilestones: allFunnelHeaderMilestones,
      navigation: {
        previousNavigationPage: {
          pagePath: summaryPagePath,
        },
        nextNavigationPage: {
          pagePath: applicationUploadIdPagePath,
        },
      },
      dataAnalytics: {
        funnelStepNumber:
          TermLifeInsuranceDigitalApplicationFunnelStepNumber.APPLICATION_PDF,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationUploadIdPagePath,
    loadComponent: async () =>
      (await import("../../shared/pages/upload-id/upload-id.page"))
        .UploadIdPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_UPLOAD_ID,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Ausweis`),
      pageTitle: $localize`Ausweis hochladen`,
      pageSubtitle: $localize`Zur Identitätsprüfung benötigen wir ein Foto von einem gültigen Ausweis.`,
      funnelHeaderMilestones: allFunnelHeaderMilestones,
      navigation: {
        previousNavigationPage: {
          pagePath: applicationPdfPagePath,
        },
        nextNavigationPage: undefined, // The user is being redirected to Skribble with a dynamic URL which is not known at compile time
      },
      dataAnalytics: {
        funnelStepNumber:
          TermLifeInsuranceDigitalApplicationFunnelStepNumber.APPLICATION_UPLOAD_ID,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationDigitalSignatureAutomaticRedirectPagePath,
    loadComponent: async () =>
      (
        await import(
          "../../shared/pages/digital-signature-automatic-redirect/digital-signature-automatic-redirect.page"
        )
      ).DigitalSignatureAutomaticRedirectPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_DIGITAL_SIGNATURE_AUTOMATIC_REDIRECT,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Automatische Weiterleitung`),
      navigation: {
        previousNavigationPage: undefined,
        nextNavigationPage: undefined, // The user is being redirected to Skribble with a dynamic URL which is not known at compile time
      },
      dataAnalytics: {
        funnelStepNumber: undefined,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationDigitalSignatureSuccessPagePath,
    loadComponent: async () =>
      (
        await import(
          "../../shared/pages/digital-signature-success/digital-signature-success.page"
        )
      ).DigitalSignatureSuccessPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_DIGITAL_SIGNATURE_SUCCESS,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Unterschrift erfolgreich`),
      pageTitle: $localize`Ihr Antrag wurde erfolgreich eingereicht`,
      pageSubtitle: $localize`Ihr Antrag ist bei uns eingegangen und wird bearbeitet. Sie erhalten Ihre Police in wenigen Tagen.`,
      funnelHeaderMilestones: allFunnelHeaderMilestones,
      navigation: {
        nextNavigationPage: {
          pagePath: startPagePath,
          queryParamsHandling: CustomQueryParamsHandling.REPLACE,
        },
      },

      dataAnalytics: {
        funnelStepNumber:
          TermLifeInsuranceDigitalApplicationFunnelStepNumber.APPLICATION_DIGITAL_SIGNATURE_SUCCESS,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationDigitalSignatureErrorPagePath,
    loadComponent: async () =>
      (
        await import(
          "../../shared/pages/digital-signature-error/digital-signature-error.page"
        )
      ).DigitalSignatureErrorPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_DIGITAL_SIGNATURE_ERROR,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Unterschrift nicht erfolgreich`),
      pageTitle: $localize`Es ist ein Fehler aufgetreten`,
      navigation: {
        nextNavigationPage: {
          pagePath: startPagePath,
          queryParamsHandling: CustomQueryParamsHandling.REPLACE,
        },
      },
      dataAnalytics: {
        funnelStepNumber: undefined,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
  {
    path: applicationDigitalSignatureDeclinedPagePath,
    loadComponent: async () =>
      (
        await import(
          "../../shared/pages/digital-signature-declined/digital-signature-declined.page"
        )
      ).DigitalSignatureDeclinedPage,
    data: {
      type: Type.PRODUCT_SPECIFIC,
      page: Page.APPLICATION_DIGITAL_SIGNATURE_DECLINED,
      product: Product.TERM_LIFE_INSURANCE_CONSTANT,
      htmlTitle: getFullHtmlTitle($localize`Unterschrift abgelehnt`),
      pageTitle: $localize`Die Unterschrift wurde abgelehnt.`,
      navigation: {
        nextNavigationPage: {
          pagePath: startPagePath,
          queryParamsHandling: CustomQueryParamsHandling.REPLACE,
        },
      },
      dataAnalytics: {
        funnelStepNumber: undefined,
      },
    },
    canActivate: [getApplicationAndCheckExpirationGuard],
  },
];
